/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment due date field — inline calendar picker for the due-date modal.
 * Mirrors AssignmentDateField pattern but uses a dedicated split input + calendar layout.
 *
 * @file AssignmentDueDateField.jsx
 * @author Anuja Gaikwad
 * @since 2.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Icon from '../icons/Icon';
import {
  parseDate,
  formatDateToStorage,
  formatMonthYear
} from '../../common/dateTimeUtils';
import { AssignmentDueDateFieldStylesRoot, classes } from './styles/assignmentDueDateFieldStyles';

const AssignmentDueDateField = ({
  value,
  onChange,
  minDate,
  maxDate,
  filterDate,
  placeholderText
}) => {
  const selectedDate = parseDate(value);
  const openToDate = selectedDate || minDate || undefined;

  const displayValue = selectedDate
    ? selectedDate.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    })
    : '';

  const handleDateChange = (date) => {
    onChange(formatDateToStorage(date));
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    decreaseYear,
    increaseYear,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    prevYearButtonDisabled,
    nextYearButtonDisabled
  }) => (
    <div className={classes.customHeader}>
      <div className={classes.navGroup}>
        <button
          type="button"
          onClick={decreaseYear}
          className={classes.navButton}
          aria-label="Previous year"
          disabled={prevYearButtonDisabled}
        >
          <Icon name="CaretDoublePrevIcon" />
        </button>
        <button
          type="button"
          onClick={decreaseMonth}
          className={classes.navButton}
          aria-label="Previous month"
          disabled={prevMonthButtonDisabled}
        >
          <Icon name="CaretPrevIcon" />
        </button>
      </div>
      <span className={classes.monthYearLabel}>
        {formatMonthYear(date)}
      </span>
      <div className={classes.navGroup}>
        <button
          type="button"
          onClick={increaseMonth}
          className={classes.navButton}
          aria-label="Next month"
          disabled={nextMonthButtonDisabled}
        >
          <Icon name="CaretNextIcon" />
        </button>
        <button
          type="button"
          onClick={increaseYear}
          className={classes.navButton}
          aria-label="Next year"
          disabled={nextYearButtonDisabled}
        >
          <Icon name="CaretDoubleNextIcon" />
        </button>
      </div>
    </div>
  );

  return (
    <AssignmentDueDateFieldStylesRoot className={classes.root}>
      <div className={classes.inputRow}>
        <input
          type="text"
          readOnly
          className={classes.input}
          value={displayValue}
          placeholder={placeholderText}
          aria-label="Due date"
        />
        <Icon name="CalendarIcon" className={classes.inputIcon} />
      </div>
      <div className={classes.calendarWrapper}>
        <DatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          inline
          minDate={minDate || (filterDate ? undefined : new Date())}
          maxDate={maxDate || undefined}
          filterDate={filterDate || undefined}
          openToDate={openToDate}
          calendarStartDay={0}
          renderCustomHeader={renderCustomHeader}
        />
      </div>
    </AssignmentDueDateFieldStylesRoot>
  );
};

AssignmentDueDateField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  filterDate: PropTypes.func,
  placeholderText: PropTypes.string
};

AssignmentDueDateField.defaultProps = {
  value: '',
  minDate: null,
  maxDate: null,
  filterDate: null,
  placeholderText: 'mm/dd/yyyy'
};

export default AssignmentDueDateField;
